import type { Player } from '../../types/player';
import { adjustPlayerScore, type PlayerVote } from './feedbackAdjustment';

/**
 * Constrained Ranking Engine for Fantasy Football
 * Two-stage scorer with positional guardrails to ensure market alignment
 */

type Pos = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DEF';

const FORMAT_ALIASES: Record<string, keyof typeof MARKET_TARGETS> = {
  superflex: 'superflex',
  'super flex': 'superflex',
  'sf': 'superflex',
  '2qb': '2qb',
  '2-qb': '2qb',
  'twoqb': '2qb',
  standard: 'standard',
  bestball: 'bestball',
  'best ball': 'bestball',
  dynasty: 'dynasty'
};

function normalizeFormat(input?: string): keyof typeof MARKET_TARGETS {
  if (!input) return 'standard';
  const key = input.trim().toLowerCase();
  return FORMAT_ALIASES[key] ?? (FORMAT_ALIASES[key.replace(/\s+/g, '')] ?? 'standard');
}

interface ScoredPlayer extends Player {
  score: number;
  warScore: number;
  roleGated: boolean;
}

// Market targets by format
const MARKET_TARGETS = {
  standard: { QB: 1, RB: 10, WR: 9, TE: 3, K: 1, DEF: 0 },
  superflex: { QB: 9, RB: 7, WR: 6, TE: 2, K: 0, DEF: 0 },
  '2qb': { QB: 12, RB: 5, WR: 5, TE: 1, K: 1, DEF: 0 },
  bestball: { QB: 3, RB: 8, WR: 10, TE: 3, K: 0, DEF: 0 },
  dynasty: { QB: 5, RB: 6, WR: 10, TE: 3, K: 0, DEF: 0 }
};

// Bounds (min, max) per position for each format - STRICT enforcement for perfect market alignment
const BOUNDS = {
  standard: { QB: [1, 1], RB: [10, 10], WR: [9, 9], TE: [3, 3], K: [1, 1], DEF: [0, 0] }, // EXACT 1/10/9/3/1 distribution
  superflex: { QB: [9, 9], RB: [7, 7], WR: [6, 6], TE: [2, 2], K: [0, 0], DEF: [0, 0] }, // EXACT 9/7/6/2 distribution
  '2qb': { QB: [12, 12], RB: [5, 5], WR: [5, 5], TE: [1, 1], K: [1, 1], DEF: [0, 0] }, // EXACT 12/5/5/1/1 distribution
  bestball: { QB: [3, 3], RB: [8, 8], WR: [10, 10], TE: [3, 3], K: [0, 0], DEF: [0, 0] }, // EXACT 3/8/10/3 distribution for best ball
  dynasty: { QB: [5, 5], RB: [6, 6], WR: [10, 10], TE: [3, 3], K: [0, 0], DEF: [0, 0] } // EXACT 5/6/10/3 distribution for dynasty
};

// Replacement ranks for 12-team leagues - tuned for better distribution
const REPLACEMENT_RANKS = {
  standard: { QB: 18, RB: 20, WR: 28, TE: 10, K: 12, DEF: 12 },
  superflex: { QB: 20, RB: 18, WR: 26, TE: 8, K: 12, DEF: 12 }, // Lower RB/TE replacement to boost value  
  '2qb': { QB: 24, RB: 20, WR: 28, TE: 10, K: 12, DEF: 12 },
  bestball: { QB: 19, RB: 38, WR: 48, TE: 18, K: 0, DEF: 0 }, // Best ball specific replacement levels
  dynasty: { QB: 18, RB: 24, WR: 30, TE: 12, K: 0, DEF: 0 } // Dynasty specific replacement levels
};

// Position scarcity weights by format - AGGRESSIVE tuning for market alignment
const POSITION_WEIGHTS = {
  standard: { QB: 0.15, RB: 3.0, WR: 1.0, TE: 2.5, K: 0.8, DEF: 0.8 }, // Massive QB penalty, huge RB/TE boost
  superflex: { QB: 1.1, RB: 1.3, WR: 1.0, TE: 1.6, K: 0.7, DEF: 0.7 }, // Boost RB/TE, reduce QB
  '2qb': { QB: 1.5, RB: 0.9, WR: 0.9, TE: 1.0, K: 0.6, DEF: 0.6 },
  bestball: { QB: 0.85, RB: 1.0, WR: 0.92, TE: 1.25, K: 0.0, DEF: 0.0 }, // Best ball specific weights - boost TE, reduce WR slightly
  dynasty: { QB: 1.0, RB: 0.8, WR: 1.2, TE: 1.1, K: 0.0, DEF: 0.0 } // Dynasty specific weights - no K/DEF
};

const DYNAMIC_TOWARD_TARGET = 0.15;

const by = <T>(sel: (t: T) => number, dir: 'desc' | 'asc' = 'desc') =>
  (a: T, b: T) => dir === 'desc' ? sel(b) - sel(a) : sel(a) - sel(b);

function dedupeByName(players: Player[]): Player[] {
  const seen = new Set<string>();
  const result: Player[] = [];
  
  for (const player of players) {
    const key = player.name.trim().toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(player);
    }
  }
  
  return result;
}

function groupByPosition(players: Player[]) {
  const groups: Record<string, Player[]> = {};
  for (const player of players) {
    const pos = mapPosition(player.position);
    if (!groups[pos]) groups[pos] = [];
    groups[pos].push(player);
  }
  return groups;
}

function mapPosition(pos: string): Pos {
  switch (pos.toUpperCase()) {
    case 'QB': return 'QB';
    case 'RB': return 'RB'; 
    case 'WR': return 'WR';
    case 'TE': return 'TE';
    case 'K': return 'K';
    case 'DEF':
    case 'DST':
    case 'D/ST': return 'DEF';
    default: return 'WR'; // Default fallback
  }
}

function computeReplacementLevels(players: Player[], format: string) {
  const replacementRanks = REPLACEMENT_RANKS[format as keyof typeof REPLACEMENT_RANKS] || REPLACEMENT_RANKS.standard;
  const groups = groupByPosition(players);
  const replacement: Record<Pos, number> = {} as Record<Pos, number>;
  
  for (const [pos, rank] of Object.entries(replacementRanks)) {
    const posPlayers = groups[pos as Pos] || [];
    posPlayers.sort(by(p => getProjectedPoints(p), 'desc'));
    
    const replacementPlayer = posPlayers[rank - 1];
    replacement[pos as Pos] = replacementPlayer ? getProjectedPoints(replacementPlayer) : 0;
  }
  
  return replacement;
}

function getProjectedPoints(player: Player): number {
  return player.projection || player.projectedPoints || player.value || 0;
}

function applyRoleGate(player: Player): number {
  // Known backup/questionable players get severe penalties
  const backupPlayers = [
    'sam darnold', 'joe flacco', 'cooper rush', 'tyler huntley', 'mac jones', 'jameis winston',
    'ian thomas', 'durham smythe', 'mike gesicki',
    'samaje perine', 'ty johnson', 'justice hill', 'dare ogunbowale', 'zack moss',
    'spencer rattler', 'bryce young', 'kirk cousins', 'jacoby brissett'
  ];
  
  // High volatility players - questionable situations, new systems, injury concerns
  const volatileQBs = [
    'will levis', 'deshaun watson', 'russell wilson', 'daniel jones', 'bryce young',
    'aidan o\'connell', 'gardner minshew', 'jacoby brissett', 'marcus mariota',
    'sam darnold', 'spencer rattler', 'kirk cousins', 'jameis winston'
  ];
  
  const volatileRBs = [
    'ezekiel elliott', 'leonard fournette', 'kareem hunt', 'jerick mckinnon',
    'boston scott', 'pierre strong', 'malik davis'
  ];
  
  const volatileWRs = [
    'hunter renfrow', 'juju smith-schuster', 'nelson agholor', 'corey davis',
    'kenny golladay', 'allen robinson', 'jarvis landry'
  ];
  
  const name = player.name.toLowerCase();
  const isBackup = backupPlayers.some(backup => name.includes(backup));
  
  if (isBackup) {
    return 0.05; // 95% penalty for known backups (was 90%)
  }
  
  // Check for high volatility players and apply dampeners
  const pos = mapPosition(player.position);
  let volatilityPenalty = 1.0;
  
  if (pos === 'QB' && volatileQBs.some(vqb => name.includes(vqb))) {
    volatilityPenalty = 0.1; // 90% penalty for volatile QBs (was 70%)
  } else if (pos === 'RB' && volatileRBs.some(vrb => name.includes(vrb))) {
    volatilityPenalty = 0.5; // 50% penalty for volatile RBs
  } else if (pos === 'WR' && volatileWRs.some(vwr => name.includes(vwr))) {
    volatilityPenalty = 0.6; // 40% penalty for volatile WRs
  }
  
  // Base role confidence by position - AGGRESSIVE QB penalty
  const roleConfidence = {
    QB: 0.3,   // Massive reduction for QBs (was 0.85)
    RB: 0.9,   // Boost for RBs (was 0.75)
    WR: 0.85,  // Boost for WRs (was 0.75)
    TE: 0.9,   // Boost for TEs (was 0.8)
    K: 0.9,
    DEF: 0.9
  } as const;
  
  const baseConfidence = roleConfidence[pos] || 0.75;
  return baseConfidence * volatilityPenalty;
}

function scorePlayers(players: Player[], replacementLevels: Record<Pos, number>, format: string, playerFeedback?: Record<string, PlayerVote[]>): ScoredPlayer[] {
  const cleanPlayers = dedupeByName(players.filter(p => ['QB', 'RB', 'WR', 'TE'].includes(mapPosition(p.position))));
  const weights = POSITION_WEIGHTS[format as keyof typeof POSITION_WEIGHTS] || POSITION_WEIGHTS.standard;
  
  return cleanPlayers.map(player => {
    const pos = mapPosition(player.position);
    const projectedPoints = getProjectedPoints(player);
    const roleGate = applyRoleGate(player);
    
    // Format specific scoring
    let baseScore: number;
    if (format === 'bestball') {
      baseScore = calculateBestBallScore(player, projectedPoints);
    } else if (format === 'dynasty') {
      baseScore = calculateDynastyScore(player, projectedPoints);
    } else {
      baseScore = projectedPoints;
    }
    
    const gatedPoints = baseScore * roleGate;
    
    // Enhanced VORP calculation - Use actual replacement level points
    const replacement = replacementLevels[pos] || 0;
    let vorpScore = Math.max(0, gatedPoints - replacement);
    
    // Apply position scarcity multiplier
    const scarcity = weights[pos] || 1.0;
    vorpScore *= scarcity;
    
    // Best ball specific market blending (75% model, 25% market)
    let marketValue = 0;
    if (player.rank && player.rank < 300) {
      // Convert ADP rank to value scale (higher rank = lower value)
      marketValue = Math.max(0, 300 - player.rank) / 3; // Normalize market value
    }
    
    const modelWeight = format === 'bestball' ? 0.75 : 0.7;
    const marketWeight = format === 'bestball' ? 0.25 : 0.3;
    let blendedScore = (modelWeight * vorpScore) + (marketWeight * marketValue);
    
    // QB soft cap penalty for superflex - after 9th QB, apply escalating penalty
    if (format === 'superflex' && pos === 'QB') {
      const qbRank = getQBMarketRank(player);
      if (qbRank > 9) {
        const penalty = Math.pow(0.85, qbRank - 9); // Exponential decay after QB9
        blendedScore *= penalty;
      }
    }
    
    // Format specific soft alignment penalty/reward
    if (format === 'bestball') {
      blendedScore = applySoftAlignment(blendedScore, pos, format);
    } else if (format === 'dynasty') {
      blendedScore = applyDynastyAlignment(blendedScore, pos, format);
    }
    
    // Tie-breaker using projected points
    blendedScore += 0.001 * gatedPoints;

    // Apply community feedback adjustment if available
    let adjustedScore = blendedScore;
    if (playerFeedback && playerFeedback[player.id]) {
      const votes = playerFeedback[player.id];
      const adjustment = adjustPlayerScore(blendedScore, votes, {
        maxMove: 5,           // Conservative max movement of 5 points
        responsiveness: 0.8,  // Moderate responsiveness to feedback
        priorN: 25,          // Conservative prior (higher = less reactive)
        minEffectiveN: 3,    // Minimum 3 votes before adjusting
        consensusBoost: {
          strength: 0.4,     // Moderate consensus boost
          start: 0.65        // Need 65% agreement for boost
        }
      });
      adjustedScore = adjustment.adjustedScore;

      // Store feedback metadata on player object for debugging/transparency
      (player as any).feedbackAdjustment = {
        originalScore: blendedScore,
        adjustedScore: adjustedScore,
        delta: adjustment.delta,
        voteCount: votes.length,
        debug: adjustment.debug
      };
    }

    return {
      ...player,
      score: adjustedScore,
      warScore: vorpScore,
      roleGated: roleGate < 0.8
    };
  });
}

// Helper function to determine QB market rank for penalty application
function getQBMarketRank(player: Player): number {
  const qbTiers = [
    ['josh allen', 'lamar jackson', 'jalen hurts'], // Tier 1 (1-3)
    ['patrick mahomes', 'joe burrow', 'dak prescott'], // Tier 2 (4-6) 
    ['tua tagovailoa', 'cj stroud', 'jayden daniels'], // Tier 3 (7-9)
    ['brock purdy', 'trevor lawrence', 'jordan love'], // Tier 4 (10-12)
    ['anthony richardson', 'caleb williams', 'kyler murray'] // Tier 5 (13-15)
  ];
  
  const name = player.name.toLowerCase();
  
  for (let tier = 0; tier < qbTiers.length; tier++) {
    for (let i = 0; i < qbTiers[tier].length; i++) {
      if (qbTiers[tier][i].split(' ').some(namePart => name.includes(namePart))) {
        return tier * 3 + i + 1; // Convert to 1-15 ranking
      }
    }
  }
  
  return 20; // Default for unlisted QBs
}

/**
 * Calculate best ball specific score focusing on ceiling and volatility
 */
function calculateBestBallScore(player: Player, projectedPoints: number): number {
  const pos = mapPosition(player.position);
  
  // Best ball scoring profile - ceiling focused
  const BESTBALL_PROFILE = {
    ceilingWeight: 0.55,    // 80th/90th percentile weekly points
    medianWeight: 0.20,     // keep some floor
    volWeight: 0.15,        // volatility/spike rate
    playoffBoost: 0.10,     // weeks 15-17 expected points
    injuryRiskCap: 0.12     // max negative from injury risk
  };
  
  // Estimate ceiling points (90th percentile) - roughly 1.3-1.5x median for skill positions
  const ceilingMultipliers: Record<Pos, number> = {
    QB: 1.25,  // QBs more consistent
    RB: 1.4,   // RBs can have huge games
    WR: 1.5,   // WRs most volatile
    TE: 1.3,   // TEs moderately volatile
    K: 1.1,    // Kickers consistent
    DEF: 1.2   // Defenses moderately volatile
  };
  
  const ceilingPts = projectedPoints * (ceilingMultipliers[pos] || 1.3);
  const medianPts = projectedPoints;
  
  // Estimate volatility based on position and player profile
  const volatility = calculateVolatility(player, pos);
  
  // Playoff boost for consistent performers
  const playoffPts = projectedPoints * (isPlayoffReliable(player) ? 1.1 : 0.95);
  
  // Calculate raw best ball score
  const rawScore = 
    BESTBALL_PROFILE.ceilingWeight * ceilingPts +
    BESTBALL_PROFILE.medianWeight * medianPts +
    BESTBALL_PROFILE.volWeight * volatility +
    BESTBALL_PROFILE.playoffBoost * playoffPts;
  
  // Apply injury risk adjustment
  const injuryRisk = calculateInjuryRisk(player);
  const injuryAdj = Math.max(1 - Math.min(injuryRisk, BESTBALL_PROFILE.injuryRiskCap), 0.85);
  
  return rawScore * injuryAdj;
}

/**
 * Calculate volatility score for best ball
 */
function calculateVolatility(player: Player, pos: Pos): number {
  // Base volatility by position
  const baseVolatility: Record<Pos, number> = {
    QB: 15,   // Lower volatility
    RB: 25,   // Moderate volatility
    WR: 35,   // High volatility - boom/bust
    TE: 20,   // Moderate volatility
    K: 8,     // Very low volatility
    DEF: 18   // Moderate volatility
  };
  
  let volatility = baseVolatility[pos] || 20;
  
  // Boost for young/breakout candidates
  const age = player.age || 27;
  if (age < 25) volatility *= 1.2; // Youth upside
  
  // Boost for players in high-powered offenses
  const highPoweredOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA', 'CIN', 'PHI'];
  if (highPoweredOffenses.includes(player.team)) {
    volatility *= 1.15;
  }
  
  return volatility;
}

/**
 * Check if player is playoff reliable
 */
function isPlayoffReliable(player: Player): boolean {
  // Veterans and proven performers get playoff boost
  const age = player.age || 27;
  const experience = age > 26;
  
  // Elite players by name recognition
  const elitePlayers = [
    'josh allen', 'lamar jackson', 'christian mccaffrey', 'tyreek hill',
    'stefon diggs', 'travis kelce', 'cooper kupp', 'davante adams'
  ];
  
  const name = player.name.toLowerCase();
  const isElite = elitePlayers.some(elite => name.includes(elite.split(' ')[1]));
  
  return experience || isElite;
}

/**
 * Calculate injury risk
 */
function calculateInjuryRisk(player: Player): number {
  const pos = mapPosition(player.position);
  const age = player.age || 27;
  
  // Base injury risk by position
  const baseRisk: Record<Pos, number> = {
    QB: 0.05,  // Low risk
    RB: 0.15,  // High risk
    WR: 0.08,  // Moderate risk
    TE: 0.10,  // Moderate risk
    K: 0.02,   // Very low risk
    DEF: 0.05  // Low risk
  };
  
  let risk = baseRisk[pos] || 0.08;
  
  // Age penalty
  if (age > 30) risk += 0.03;
  if (age > 32) risk += 0.02;
  
  return Math.min(risk, 0.20); // Cap at 20%
}

/**
 * Apply soft alignment for best ball to encourage proper position distribution
 */
function applySoftAlignment(score: number, pos: Pos, format: string): number {
  if (format !== 'bestball') return score;
  
  // Soft alignment penalties/rewards to guide toward 3/8/10/3 distribution
  const alignmentFactors: Record<Pos, number> = {
    QB: 1.0,    // Neutral
    RB: 1.0,    // Neutral
    WR: 0.98,   // Slight penalty to reduce from 13 to 10
    TE: 1.05,   // Boost to increase from 0 to 3
    K: 0.0,     // No kickers in best ball top 24
    DEF: 0.0    // No defenses in best ball top 24
  };
  
  return score * (alignmentFactors[pos] || 1.0);
}

/**
 * Calculate dynasty specific score focusing on age curves and multi-year value
 */
function calculateDynastyScore(player: Player, projectedPoints: number): number {
  const pos = mapPosition(player.position);
  const age = player.age || 27;
  
  // Dynasty scoring profile - multi-year value focused
  const DYNASTY_PROFILE = {
    war3yrWeight: 0.50,     // 3-year weighted WAR
    vorWeight: 0.20,        // value over replacement
    ageCurveWeight: 0.15,   // age/longevity multiplier
    prospectWeight: 0.10,   // draft capital/upside
    marketWeight: 0.05      // market anchor
  };
  
  // Calculate 3-year weighted WAR (Year1*3, Year2*2, Year3*1)
  const war3yr = calculateWAR3Year(player, projectedPoints);
  
  // Age curve multiplier based on position-specific peaks and decay
  const ageCurve = calculateAgeCurve(pos, age);
  
  // Prospect capital for rookies and young players
  const prospectCapital = calculateProspectCapital(player);
  
  // Market anchor (light weighting)
  const marketAnchor = calculateMarketAnchor(player);
  
  // Calculate dynasty score
  const dynastyScore = 
    DYNASTY_PROFILE.war3yrWeight * war3yr +
    DYNASTY_PROFILE.ageCurveWeight * (ageCurve - 1.0) * 100 + // Center around 0
    DYNASTY_PROFILE.prospectWeight * prospectCapital +
    DYNASTY_PROFILE.marketWeight * marketAnchor;
  
  // Base it on projected points but apply dynasty multipliers
  return projectedPoints * (1 + dynastyScore / 100);
}

/**
 * Calculate 3-year weighted WAR for dynasty
 */
function calculateWAR3Year(player: Player, projectedPoints: number): number {
  // Estimate 3-year value with decay
  const year1 = projectedPoints * 3;  // Current year weight
  const year2 = projectedPoints * 0.9 * 2;  // 10% decay, weight 2
  const year3 = projectedPoints * 0.8 * 1;  // 20% decay, weight 1
  
  return (year1 + year2 + year3) / 6; // Normalize by total weights
}

/**
 * Calculate age curve multiplier for dynasty
 */
function calculateAgeCurve(pos: Pos, age: number): number {
  // Clamp age to reasonable bounds
  if (age < 18 || age > 40) return 0.85;
  
  // Position-specific peak windows and decay rates
  const peakWindows: Record<Pos, [number, number]> = {
    QB: [25, 32],   // Long peak window
    RB: [23, 26],   // Short peak window
    WR: [24, 28],   // Moderate peak window
    TE: [25, 29],   // Moderate peak window
    K: [25, 35],    // Very long peak
    DEF: [25, 30]   // Moderate peak
  };
  
  const halfLife: Record<Pos, number> = {
    QB: 6,   // Gentle decay
    RB: 3,   // Steep decay
    WR: 4,   // Moderate decay
    TE: 4,   // Moderate decay
    K: 8,    // Very gentle decay
    DEF: 5   // Moderate decay
  };
  
  const [peakStart, peakEnd] = peakWindows[pos] || [24, 28];
  const decay = halfLife[pos] || 4;
  
  let multiplier: number;
  
  if (age < peakStart) {
    // Ramp up to peak
    multiplier = 0.9 + 0.02 * (age - (peakStart - 5));
  } else if (age <= peakEnd) {
    // Peak years
    multiplier = 1.05;
  } else {
    // Decay after peak
    multiplier = 1.05 * Math.pow(0.5, (age - peakEnd) / decay);
  }
  
  return Math.max(0.75, Math.min(1.10, multiplier));
}

/**
 * Calculate prospect capital for rookies and young players
 */
function calculateProspectCapital(player: Player): number {
  const age = player.age || 27;
  
  // Only apply to young players (rookies and 2nd year)
  if (age > 25) return 0;
  
  // Mock draft capital based on player profile
  // In real implementation, this would come from actual draft data
  const draftCapitalByRound = [0, 1.00, 0.60, 0.35, 0.20, 0.15, 0.10, 0.05];
  
  // Estimate draft round based on player name/profile (mock)
  let estimatedRound = 4; // Default mid-round
  
  // High-profile rookies (would be actual draft data)
  const firstRoundNames = ['caleb williams', 'jayden daniels', 'marvin harrison', 'malik nabers', 'rome odunze'];
  const secondRoundNames = ['brock bowers', 'brian thomas', 'keon coleman'];
  
  const name = player.name.toLowerCase();
  
  if (firstRoundNames.some(n => name.includes(n.split(' ')[1]))) {
    estimatedRound = 1;
  } else if (secondRoundNames.some(n => name.includes(n.split(' ')[1]))) {
    estimatedRound = 2;
  }
  
  let bonus = draftCapitalByRound[Math.min(7, estimatedRound)] || 0;
  
  // Position-specific bonuses
  const pos = mapPosition(player.position);
  if ((pos === 'WR' || pos === 'RB') && age <= 22) {
    bonus += 0.10; // Early declare bonus
  }
  
  return Math.min(bonus, 1.10);
}

/**
 * Calculate market anchor for dynasty
 */
function calculateMarketAnchor(player: Player): number {
  // Light market weighting based on ADP/consensus
  if (player.rank && player.rank < 200) {
    return Math.max(0, 200 - player.rank) / 20; // Normalize to 0-10 scale
  }
  return 0;
}

/**
 * Apply dynasty alignment to encourage proper position distribution
 */
function applyDynastyAlignment(score: number, pos: Pos, format: string): number {
  if (format !== 'dynasty') return score;
  
  // Soft alignment penalties/rewards to guide toward 5/6/10/3 distribution
  const alignmentFactors: Record<Pos, number> = {
    QB: 0.95,   // Slight penalty to reduce QB count
    RB: 1.05,   // Boost to increase RB count
    WR: 1.0,    // Neutral
    TE: 1.03,   // Slight boost for TEs
    K: 0.0,     // No kickers in dynasty top 24
    DEF: 0.0    // No defenses in dynasty top 24
  };
  
  return score * (alignmentFactors[pos] || 1.0);
}

function selectTop24(scoredPlayers: ScoredPlayer[], format: string) {
  const fmt = normalizeFormat(format);
  const target = MARKET_TARGETS[fmt] || MARKET_TARGETS.standard;
  const bounds = BOUNDS[fmt] || BOUNDS.standard;

  const SKILL_POS: Pos[] = ['QB', 'RB', 'WR', 'TE'];

  // Build groups and sort by score
  const groups = groupByPosition(scoredPlayers as Player[]);
  for (const pos of SKILL_POS) {
    (groups[pos] ??= []);
    groups[pos].sort(by(p => (p as ScoredPlayer).score, 'desc'));
  }

  const chosen: Player[] = [];
  const counts: Record<Pos, number> = { QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DEF: 0 };
  const used = new Set<string>();

  console.log(`📊 Selecting top 24 for ${fmt} with bounds:`, bounds);
  console.log(`📊 Available players by position:`, Object.fromEntries(
    SKILL_POS.map(pos => [pos, (groups[pos] || []).length])
  ));

  // Exact distribution branch for ALL formats (including standard, bestball, and dynasty)
  if (fmt === 'superflex' || fmt === '2qb' || fmt === 'standard' || fmt === 'bestball' || fmt === 'dynasty') {
    for (const pos of SKILL_POS) {
      const want = (target[pos] ?? 0);
      if (want <= 0) continue;

      const pool = groups[pos] || [];
      console.log(`📊 Selecting exactly ${want} ${pos}s from ${pool.length} available`);
      
      for (let i = 0; i < want && i < pool.length; i++) {
        const p = pool[i];
        if (!used.has(p.name)) {
          chosen.push(p);
          counts[pos]++;
          used.add(p.name);
          console.log(`✅ Added ${pos} ${i+1}/${want}: ${p.name} (score: ${(p as ScoredPlayer).score?.toFixed(2)})`);
        }
      }
    }
    console.log(`📊 Final counts:`, counts);
    return { chosen, counts };
  }

  // Generic branch (other formats):
  // Step 1: satisfy minimums
  for (const pos of SKILL_POS) {
    const [min] = bounds[pos] ?? [0, 0];
    const pool = groups[pos] || [];
    for (let i = 0; i < min && i < pool.length; i++) {
      const p = pool[i];
      if (!used.has(p.name)) {
        chosen.push(p);
        counts[pos]++;
        used.add(p.name);
      }
    }
  }

  // Step 2: fill to 24 with dynamic bias *and* hard caps from bounds
  while (chosen.length < 24) {
    let best: ScoredPlayer | null = null;
    let bestScore = -Infinity;

    for (const candidate of scoredPlayers) {
      if (used.has(candidate.name)) continue;
      const pos = mapPosition(candidate.position);
      if (!SKILL_POS.includes(pos)) continue;

      const [ , max ] = bounds[pos] ?? [0, (target[pos] ?? 0)]; // cap to target if bounds missing
      if (counts[pos] >= max) continue;

      const cur = counts[pos];
      const want = target[pos] ?? 0;
      const under = Math.max(0, want - cur);
      const bias = 1 + DYNAMIC_TOWARD_TARGET * (want > 0 ? under / want : 0);

      const s = candidate.score * bias;
      if (s > bestScore) {
        bestScore = s;
        best = candidate;
      }
    }

    if (!best) break;
    chosen.push(best);
    counts[mapPosition(best.position)]++;
    used.add(best.name);
  }

  return { chosen, counts };
}

export function buildConstrainedRankings(players: Player[], format: string, playerFeedback?: Record<string, PlayerVote[]>) {
  const fmt = normalizeFormat(format);
  console.log(`🎯 CONSTRAINED ENGINE: Building constrained rankings for ${fmt}...`);
  console.log(`🎯 CONSTRAINED ENGINE: Input players: ${players.length}`);
  
  // Calculate replacement levels
  const replacementLevels = computeReplacementLevels(players, fmt);
  
  // Score all players
  const scoredPlayers = scorePlayers(players, replacementLevels, fmt, playerFeedback);
  
  // Select top 24 with constraints
  const { chosen: top24, counts } = selectTop24(scoredPlayers, fmt);
  
  // Rank remaining players by score
  const remaining = scoredPlayers
    .filter(p => !top24.some(t => t.name === p.name))
    .sort(by(p => p.score, 'desc'));
  
  // Combine and assign final ranks
  const finalRankings = [...top24, ...remaining].map((player, index) => ({
    ...player,
    rank: index + 1,
    constrainedRank: index < 24
  }));
  
  // Calculate alignment
  const target = MARKET_TARGETS[fmt] || MARKET_TARGETS.standard;
  let alignmentScore = 0;
  let totalPositions = 0;
  
  for (const [pos, targetCount] of Object.entries(target)) {
    if (targetCount > 0) {
      const currentCount = counts[pos as Pos] || 0;
      const diff = Math.abs(targetCount - currentCount);
      alignmentScore += diff === 0 ? 1 : Math.max(0, 1 - diff * 0.5);
      totalPositions++;
    }
  }
  
  const alignmentPct = totalPositions > 0 ? (alignmentScore / totalPositions) * 100 : 100;
  
  console.log(`✅ ${fmt} Constrained Rankings:`, {
    target,
    actual: counts,
    alignmentPct: Math.round(alignmentPct)
  });
  
  return {
    rankedPlayers: finalRankings,
    positionCounts: counts,
    target,
    alignmentPct,
    replacementLevels
  };
}